<template>
	<!-- No `label`: the card's "Delivery info" heading names the list. -->
	<Timeline :steps="steps" :muted="muted">
		<template #icon="{ step }">
			<!-- A static glyph from src/assets, inlined so it takes the node's colour (currentColor). -->
			<span
				v-if="glyphs[step.id]"
				class="order-timeline__glyph"
				:class="{ 'order-timeline__glyph--flipped': glyphs[step.id]!.flipped }"
				:data-glyph="glyphs[step.id]!.name"
				v-html="glyphs[step.id]!.svg"
			/>
		</template>
	</Timeline>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Timeline, type TimelineStep, type TimelineStepTone } from '@simple-life-app/growth-ui-kova'
import type { TimelineItem, TimelineState } from '@/orders/detailModel'
import { timelineGlyph, type OrderGlyph } from './orderGlyphs'

/**
 * "Delivery info"'s timeline (Figma `Timeline/Vertical`, e.g. 14254:161780): the model's rows drawn by the
 * DS `Timeline`. Every decision — which rows, their state, time, description, glyph, the greyed-out terminal
 * order — is `buildOrderDetailModel`'s; this only translates the vocabulary. A returned order's last row is
 * the DS's `failed` look (grey node, the return glyph), as Figma draws it (14254:166650).
 */

const props = defineProps<{
	items: TimelineItem[]
	muted: boolean
}>()

const TONE: Record<TimelineState, TimelineStepTone> = {
	done: 'done',
	upcoming: 'upcoming',
	failed: 'failed',
	returned: 'failed',
}

const steps = computed<TimelineStep[]>(() =>
	props.items.map((item) => ({
		id: item.key,
		title: item.title,
		caption: item.timestamp ?? undefined,
		description: item.description,
		tone: TONE[item.state],
		compact: item.small,
	})),
)

/** Row key → glyph; a row is in the timeline once, so its key identifies it. */
const glyphs = computed(
	() =>
		Object.fromEntries(props.items.map((item) => [item.key, timelineGlyph(item)])) as Record<string, OrderGlyph | null>,
)
</script>

<style scoped>
/* Fills the DS's 20px glyph box (the DS sizes the SVG to it), in the node's colour: white on a reached step,
   grey on an upcoming one. */
.order-timeline__glyph {
	display: block;
	width: 100%;
	height: 100%;
}

/* Figma's icon-checkmark is drawn upside down; the frames flip it (as ConsequenceList does). */
.order-timeline__glyph--flipped {
	transform: scaleY(-1);
}
</style>
